import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'

import { userDetailsFromQuery, worldDetailsFromQuery } from '../../transforms/detailsFromQueries'
import type { UserDetails, WorldDetails } from '../../types'

export default function useAllWorlds<K extends (keyof WorldDetails)[]>(
  fields: [...K],
  params: {
    numRandomUsersToInclude: number
    adminIncludeAll: boolean
  } = {
    numRandomUsersToInclude: 0,
    adminIncludeAll: false,
  }
): (Pick<WorldDetails, K[number]> & {
  users: Pick<UserDetails, 'id' | 'avatarUrl' | 'displayName'>[]
})[] {
  const user = useUser()
  const query = useQuery()

  const { numRandomUsersToInclude, adminIncludeAll } = params

  const worlds = query.many(
    WorldQ.where(
      {
        include: [{ association: 'Course', required: false }],
        order: [[WorldQ.position, 'asc']],
      },
      SQL`(${WorldQ.isVisible()} OR ${WorldQ.isUserMember(user.id)} OR ${
        adminIncludeAll ? SQL`TRUE` : SQL`FALSE`
      })`
    )
  )

  return worlds.map(world => {
    const randomUsers =
      numRandomUsersToInclude === 0
        ? []
        : world.many`
      ${WorldMemberQ.where({
        worldId: WorldQ.id,
        include: [{ association: 'User', required: true }],
        tableSample: SQL`BERNOULLI(10)`,
      })}
      LIMIT ${numRandomUsersToInclude}
    `
    return {
      ...worldDetailsFromQuery(fields, { world, userId: user.id }),
      users: randomUsers.map(randomUser =>
        userDetailsFromQuery(['id', 'avatarUrl', 'displayName'], { user: randomUser })
      ),
    }
  })
}
